import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Pill from "../../components/Kids/Pill";
import Button from "../../components/Kids/Button";

const API = process.env.REACT_APP_API_URL;

export default function KidsSettings() {
  const { t }     = useTranslation();
  const navigate = useNavigate();
  const token    = localStorage.getItem("kids_parent_token");
  const auth     = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);

  // Profile
  const [profile, setProfile]   = useState({ name: "", email: "", phone: "", country: "", city: "", school: "", referral_code: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  // Password
  const [pwd, setPwd]           = useState({ current_password: "", password: "", password_confirmation: "" });
  const [savingPwd, setSavingPwd] = useState(false);

  // Subscription
  const [sub, setSub]           = useState(null);
  const [plans, setPlans]       = useState([]);
  const [cycle, setCycle]       = useState("monthly");
  const [busySub, setBusySub]   = useState(false);
  const [pending, setPending]   = useState(null); // pending change/cancel request awaiting admin

  // Sound / voice / music — all currently share the one `kids_sound_muted`
  // flag that useGameSounds()/useKidVoice() already check (no dedicated
  // per-channel keys exist yet), so the three rows below toggle in unison.
  const [soundMuted, setSoundMuted] = useState(() => localStorage.getItem("kids_sound_muted") === "1");

  // Delete account
  const [deletePwd, setDeletePwd] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!token) { navigate("/kids/login"); return; }
    Promise.all([
      axios.get(`${API}/api/kids/parent/profile`, auth),
      axios.get(`${API}/api/kids/subscription`, auth),
      axios.get(`${API}/api/kids/plans`),
      axios.get(`${API}/api/kids/subscription/request`, auth),
    ]).then(([p, s, pl, rq]) => {
      const pd = p.data.data || {};
      setProfile({ name: pd.name || "", email: pd.email || "", phone: pd.phone || "", country: pd.country || "", city: pd.city || "", school: pd.school || "", referral_code: pd.referral_code || "" });
      const sd = s.data.data;
      setSub(sd);
      if (sd?.billing_cycle) setCycle(sd.billing_cycle);
      setPlans(pl.data.data || []);
      setPending(rq.data.data || null);
      setLoading(false);
    }).catch(() => {
      toast.error("Session expired. Please sign in again.");
      navigate("/kids/login");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    axios.post(`${API}/api/kids/parent/logout`, {}, auth).finally(() => {
      localStorage.removeItem("kids_parent_token");
      localStorage.removeItem("kids_parent_user");
      localStorage.removeItem("kids_family");
      localStorage.removeItem("kids_active_mode");
      navigate("/kids/login");
    });
  };

  const refreshPending = async () => {
    try {
      const rq = await axios.get(`${API}/api/kids/subscription/request`, auth);
      setPending(rq.data.data || null);
    } catch { /* ignore */ }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await axios.post(`${API}/api/kids/parent/profile`, {
        name: profile.name, phone: profile.phone, country: profile.country, city: profile.city, school: profile.school,
      }, auth);
      const pd = res.data.data;
      setProfile(p => ({ ...p, ...pd }));
      // keep the cached parent user fresh
      const cached = JSON.parse(localStorage.getItem("kids_parent_user") || "null");
      if (cached) localStorage.setItem("kids_parent_user", JSON.stringify({ ...cached, name: pd.name }));
      toast.success("Profile updated! ✅");
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't update profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwd.password !== pwd.password_confirmation) {
      toast.error("New passwords don't match.");
      return;
    }
    setSavingPwd(true);
    try {
      await axios.post(`${API}/api/kids/parent/password`, pwd, auth);
      setPwd({ current_password: "", password: "", password_confirmation: "" });
      toast.success("Password changed! 🔒");
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't change password.");
    } finally {
      setSavingPwd(false);
    }
  };

  const requestPlan = async (slug, planName) => {
    if (busySub) return;
    if (!window.confirm(`Request to switch to "${planName}" (${cycle})?\n\nOur team will review and activate it for you — no payment is taken here yet.`)) return;
    setBusySub(true);
    try {
      const res = await axios.post(`${API}/api/kids/subscription/upgrade`, { plan_slug: slug, billing_cycle: cycle }, auth);
      toast.success(res.data.message || "Request sent to the Cabil team!");
      await refreshPending();
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't send your request.");
    } finally {
      setBusySub(false);
    }
  };

  const requestCancel = async () => {
    if (!window.confirm("Request to cancel your subscription? Our team will process it and you'll keep access until the end of your current period.")) return;
    setBusySub(true);
    try {
      const res = await axios.post(`${API}/api/kids/subscription/cancel`, {}, auth);
      toast.success(res.data.message || "Cancellation request sent.");
      await refreshPending();
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't send your request.");
    } finally {
      setBusySub(false);
    }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    if (!window.confirm("This permanently deletes your Cabil Kids account and signs you out everywhere. Are you sure?")) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/api/kids/parent/delete-account`, { password: deletePwd }, auth);
      localStorage.removeItem("kids_parent_token");
      localStorage.removeItem("kids_parent_user");
      localStorage.removeItem("kids_family");
      localStorage.removeItem("kids_active_mode");
      toast.success("Your account has been deleted.");
      navigate("/kids");
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't delete your account.");
    } finally {
      setDeleting(false);
    }
  };

  const toggleSound = () => {
    const next = !soundMuted;
    setSoundMuted(next);
    localStorage.setItem("kids_sound_muted", next ? "1" : "0");
  };

  if (loading) return (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <div className="text-center">
        <div className="text-6xl animate-bounce mb-4">⚙️</div>
        <p className="text-brand font-display font-semibold">Loading settings…</p>
      </div>
    </div>
  );

  const currentSlug = sub?.plan?.slug;

  // is_expired comes from backend when deployed; fall back to client-side check
  // when the backend hasn't been updated yet (trial_ends date passed, or cancelled)
  const isExpired = sub?.is_expired
    ?? ((sub?.status === 'trialing' && (sub?.days_left ?? 1) === 0)
        || sub?.status === 'cancelled'
        || (sub?.status === 'active' && sub?.period_end && new Date(sub.period_end) < new Date()));

  return (
    <div className="min-h-screen font-body" style={{ background: "linear-gradient(150deg,#FFF6EE,#FFF9F4 55%,#fff)" }}>
      {/* Header */}
      <header className="bg-white shadow-card px-6 py-4 flex items-center justify-between border-b border-cardline">
        <div className="flex items-center gap-2.5">
          <span className="text-2xl leading-none">⭐</span>
          <span className="font-display font-semibold text-xl">
            <span className="text-brand">Cabil</span> <span className="text-gold">Kids</span>
          </span>
        </div>
        {isExpired ? (
          <button onClick={logout}
            className="text-sm font-body font-bold text-danger hover:opacity-80 transition flex items-center gap-1">
            ↩️ Sign out
          </button>
        ) : (
          <Link to="/kids/parent/dashboard" className="text-faint hover:text-brand text-sm font-body font-semibold transition">← Back to dashboard</Link>
        )}
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <h1 className="font-display font-semibold text-2xl text-ink">⚙️ {t("kids.settings.title")}</h1>

        {/* ── PROFILE ── */}
        <section className="bg-white border border-cardline rounded-card-lg p-6">
          <h2 className="font-body font-extrabold text-ink text-[17px] mb-4">👤 {t("kids.settings.profile")}</h2>
          <form onSubmit={saveProfile} className="grid sm:grid-cols-2 gap-4">
            <Field label={t("kids.settings.name")} value={profile.name} onChange={v => setProfile(p => ({ ...p, name: v }))} required />
            <div>
              <label className="block font-body font-extrabold text-[12px] text-muted mb-1.5">{t("kids.settings.email")}</label>
              <input value={profile.email} disabled
                className="w-full px-4 py-2.5 rounded-xl border border-[#E5DED4] bg-cream/40 text-faint cursor-not-allowed font-body" />
              <p className="text-xs text-faint mt-1">Email can't be changed (it's your verified login).</p>
            </div>
            <Field label={t("kids.settings.phone")} value={profile.phone} onChange={v => setProfile(p => ({ ...p, phone: v }))} required />
            <Field label="Country" value={profile.country} onChange={v => setProfile(p => ({ ...p, country: v }))} />
            <Field label="City" value={profile.city} onChange={v => setProfile(p => ({ ...p, city: v }))} />
            <Field label="School Name" value={profile.school} onChange={v => setProfile(p => ({ ...p, school: v }))} />
            {profile.referral_code && (
              <div className="sm:col-span-2 rounded-2xl p-4" style={{ background: "linear-gradient(90deg,#F6F2FF,#F0EBFF)", border: "1px solid #E4DAFB" }}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="font-body font-extrabold text-sm text-navy">🎁 Your Referral Code</p>
                    <p className="text-xs text-muted">Share this code with friends — they enter it at signup, and you get <b>7 extra trial days</b>!</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-white border-2 rounded-lg px-4 py-2 font-mono font-bold text-lg tracking-wider" style={{ borderColor: "#C9B8F5", color: "#7C5CFF" }}>
                      {profile.referral_code}
                    </span>
                    <button type="button" onClick={() => { navigator.clipboard.writeText(profile.referral_code); toast.success("Copied!"); }}
                      className="px-3.5 py-2 rounded-lg text-white text-sm font-body font-bold transition" style={{ background: "#7C5CFF" }}>
                      Copy
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="sm:col-span-2">
              <Button type="submit" disabled={savingProfile}>
                {savingProfile ? "Saving…" : t("kids.settings.save")}
              </Button>
            </div>
          </form>
        </section>

        {/* ── PASSWORD ── */}
        <section className="bg-white border border-cardline rounded-card-lg p-6">
          <h2 className="font-body font-extrabold text-ink text-[17px] mb-4">🔒 {t("kids.settings.password")}</h2>
          <form onSubmit={savePassword} className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Field type="password" label={t("kids.settings.currentPassword")} value={pwd.current_password}
                onChange={v => setPwd(p => ({ ...p, current_password: v }))} required />
            </div>
            <Field type="password" label={t("kids.settings.newPassword")} value={pwd.password}
              onChange={v => setPwd(p => ({ ...p, password: v }))} required />
            <Field type="password" label={t("kids.settings.confirmPassword")} value={pwd.password_confirmation}
              onChange={v => setPwd(p => ({ ...p, password_confirmation: v }))} required />
            <div className="sm:col-span-2">
              <Button type="submit" variant="secondary" disabled={savingPwd}>
                {savingPwd ? "Updating…" : t("kids.settings.updatePassword")}
              </Button>
            </div>
          </form>
        </section>

        {/* ── SOUND & VOICE ── */}
        <section className="bg-white border border-cardline rounded-card-lg p-6">
          <h2 className="font-body font-extrabold text-ink text-[17px] mb-1">🔊 Sound &amp; Voice</h2>
          <p className="text-xs text-faint mb-3">One switch controls all sound in the kid experience for now.</p>
          <div className="divide-y divide-cardline">
            <ToggleRow emoji="🔊" label="Sound FX" sub="Correct/wrong chimes, clicks, celebrations" checked={!soundMuted} onChange={toggleSound} />
            <ToggleRow emoji="🦊" label="Cabi's voice" sub="Spoken encouragement and read-alouds" checked={!soundMuted} onChange={toggleSound} />
            <ToggleRow emoji="🎵" label="Music" sub="Background music during games" checked={!soundMuted} onChange={toggleSound} />
          </div>
        </section>

        {/* ── SUBSCRIPTION ── */}
        <section className="bg-white border border-cardline rounded-card-lg p-6">
          <h2 className="font-body font-extrabold text-ink text-[17px] mb-1">💳 Subscription</h2>
          {sub ? (
            <>
              {sub.is_expired && (
                <div className="mb-5 mt-3 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 flex items-start gap-3">
                  <span className="text-xl">🚫</span>
                  <div className="text-sm">
                    <p className="font-body font-bold text-danger">
                      {sub.status === "cancelled" ? "Your subscription has been cancelled." : "Your free trial has ended."}
                    </p>
                    <p className="text-danger/90">Request a plan below — our team will activate it for you, no payment needed yet.</p>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 mb-5 mt-3">
                <span className="text-sm text-muted">Current plan:</span>
                <span className="font-body font-extrabold text-ink">{sub.plan?.name}</span>
                <span
                  className="text-[11px] font-body font-extrabold px-3 py-1 rounded-full"
                  style={
                    sub.is_expired ? { background: "rgba(229,72,77,.1)", color: "#E5484D" }
                    : sub.status === "trialing" ? { background: "#FEF6D8", color: "#8A6D0B" }
                    : sub.status === "active" ? { background: "#DFF5E6", color: "#22874E" }
                    : { background: "#F1EAE1", color: "#B0A090" }
                  }
                >
                  {sub.is_expired ? "Expired"
                    : sub.status === "trialing" ? `Trial — ${sub.days_left} days left`
                    : sub.status === "active" ? "Active"
                    : sub.status}
                </span>
                {sub.period_end && sub.status === "active" &&
                  <span className="text-xs text-faint">Renews {sub.period_end}</span>}
              </div>

              {/* Pending request banner */}
              {pending && (
                <div className="mb-5 rounded-xl border px-4 py-3 flex items-start gap-3" style={{ borderColor: "#F6DE97", background: "#FEF6D8" }}>
                  <span className="text-xl">⏳</span>
                  <div className="text-sm">
                    <p className="font-body font-bold" style={{ color: "#8A6D0B" }}>
                      {pending.type === "cancel"
                        ? "Cancellation request pending"
                        : `Request to switch to ${pending.requested_plan} (${pending.billing_cycle}) pending`}
                    </p>
                    <p style={{ color: "#A98A2E" }}>Our team will review and action this shortly. You'll be notified once it's done.</p>
                  </div>
                </div>
              )}

              {/* Billing cycle toggle */}
              <div className="inline-flex bg-white border border-cardline rounded-full p-1 mb-5 gap-1">
                {["monthly", "yearly"].map(c => (
                  <Pill key={c} active={cycle === c} onClick={() => setCycle(c)}>
                    {c === "monthly" ? "Monthly" : "Yearly (save more)"}
                  </Pill>
                ))}
              </div>

              {/* Plans */}
              <div className="grid sm:grid-cols-3 gap-4">
                {plans.map(p => {
                  const isCurrent = p.slug === currentSlug && !sub.is_expired;
                  const price = cycle === "yearly" ? p.price_yearly : p.price_monthly;
                  return (
                    <div key={p.slug} className={`rounded-card border-2 p-4 flex flex-col ${
                      isCurrent ? "border-brand bg-brand/5" : "border-[#E5DED4]"}`}>
                      <div className="font-body font-extrabold text-ink">{p.name}</div>
                      <div className="text-2xl font-body font-black text-brand my-1">
                        PKR {Number(price || 0).toLocaleString()}<span className="text-xs text-faint font-medium">/{cycle === "yearly" ? "yr" : "mo"}</span>
                      </div>
                      <div className="text-xs text-muted mb-3">Up to {p.max_children} child{p.max_children > 1 ? "ren" : ""}</div>
                      <Button
                        variant={isCurrent ? "secondary" : "primary"}
                        onClick={() => requestPlan(p.slug, p.name)}
                        disabled={busySub || isCurrent || !!pending}
                        className="mt-auto w-full !min-h-0 !py-2.5 !text-[13px]"
                      >
                        {isCurrent ? "Current plan" : pending ? "Request pending" : "Request this plan"}
                      </Button>
                    </div>
                  );
                })}
              </div>

              <p className="text-xs text-faint mt-3">
                💡 Online payment is coming soon. For now, your request goes to the Cabil team who'll activate your plan for you.
              </p>

              {/* Cancel */}
              {!sub.is_expired && (sub.status === "trialing" || sub.status === "active") && (
                <div className="mt-6 pt-5 border-t border-cardline">
                  <button onClick={requestCancel} disabled={busySub || (pending && pending.type === "cancel")}
                    className="text-danger hover:opacity-80 disabled:text-faint text-sm font-body font-bold transition">
                    {pending && pending.type === "cancel" ? "Cancellation requested" : "Request cancellation"}
                  </button>
                  <p className="text-xs text-faint mt-1">Our team will process it; you'll keep access until the end of your current period.</p>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted text-sm mt-3">No subscription found.</p>
          )}
        </section>

        {/* ── DANGER ZONE ── */}
        <section className="bg-white border border-danger/30 rounded-card-lg p-6">
          <h2 className="font-body font-extrabold text-danger text-[17px] mb-1">⚠️ Danger Zone</h2>
          <p className="text-xs text-muted mb-4">
            Deleting your account removes your access immediately and signs you out on every device.
            Your children's profiles and progress stay on file in case you come back — contact support if you'd like everything erased instead.
          </p>
          {!confirmingDelete ? (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="text-danger hover:opacity-80 text-sm font-body font-bold transition"
            >
              Delete my account
            </button>
          ) : (
            <form onSubmit={deleteAccount} className="max-w-sm space-y-3">
              <Field type="password" label="Confirm your password" value={deletePwd}
                onChange={setDeletePwd} required />
              <div className="flex gap-2">
                <button type="submit" disabled={deleting}
                  className="bg-danger hover:opacity-90 disabled:opacity-60 text-white font-body font-bold text-sm px-4 py-2.5 rounded-xl transition">
                  {deleting ? "Deleting…" : "Permanently delete account"}
                </button>
                <button type="button" onClick={() => { setConfirmingDelete(false); setDeletePwd(""); }}
                  className="text-muted hover:text-ink font-body font-bold text-sm px-4 py-2.5 transition">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="block font-body font-extrabold text-[12px] text-muted mb-1.5">{label}</label>
      <input type={type} value={value} required={required}
        onChange={e => onChange(e.target.value)}
        className="w-full px-4 py-2.5 rounded-xl border border-[#E5DED4] focus:border-brand focus:ring-1 focus:ring-brand outline-none transition font-body text-ink" />
    </div>
  );
}

function ToggleRow({ emoji, label, sub, checked, onChange }) {
  return (
    <div className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
      <div className="flex items-center gap-3">
        <span className="text-xl leading-none">{emoji}</span>
        <div>
          <div className="font-body font-extrabold text-sm text-ink">{label}</div>
          {sub && <div className="text-xs text-muted">{sub}</div>}
        </div>
      </div>
      <button
        type="button"
        onClick={onChange}
        aria-pressed={checked}
        aria-label={label}
        className={`relative w-12 h-7 rounded-full shrink-0 transition ${checked ? "bg-brand" : "bg-cardline"}`}
      >
        <span
          className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-card transition-all ${checked ? "start-[22px]" : "start-0.5"}`}
        />
      </button>
    </div>
  );
}
